import React, { useState, useEffect, useCallback, useRef } from "react";
import API_BASE from "../apiConfig";
import { useTheme, getT } from "../ThemeContext";
import { useAppConfig } from "../AppContext";
import { PrimaryButton, GhostButton } from "../components/ThemedButton";

const money = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(n || 0));

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

export default function POSRegister({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { config } = useAppConfig();
  const currency = (config?.currency || "USD").toUpperCase();
  const isAdminRole = ["Admin", "Super Admin"].includes(currentUser?.user_role);

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [registers, setRegisters] = useState([]);
  const [registerId, setRegisterId] = useState(null);
  const [session, setSession] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false); // has the current-session lookup returned yet?
  const [openingCash, setOpeningCash] = useState("");

  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catalog, setCatalog] = useState([]); // full active catalog, for category browse
  const [selectedCategoryId, setSelectedCategoryId] = useState(null); // null | "uncategorized" | number
  const [cart, setCart] = useState([]); // {key, item_type, product_id, variant_id, description, quantity, unit_price}
  const [customer, setCustomer] = useState(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const searchRef = useRef(null);

  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscountId, setSelectedDiscountId] = useState("");
  const [customDiscountOpen, setCustomDiscountOpen] = useState(false);
  const [customDiscountType, setCustomDiscountType] = useState("percentage");
  const [customDiscountValue, setCustomDiscountValue] = useState("");
  const [customDiscountReason, setCustomDiscountReason] = useState("");
  const [appliedCustomDiscount, setAppliedCustomDiscount] = useState(null); // {type, value, reason}

  const [payMethod, setPayMethod] = useState("cash"); // cash | card
  const stripeRef = useRef(null);
  const cardElRef = useRef(null);
  const cardMountedRef = useRef(false);
  const [cardReady, setCardReady] = useState(false);

  useEffect(() => {
    api("/pos/locations/").then((d) => {
      setLocations(d.locations);
      if (d.locations.length) setLocationId(d.locations[0].location_id);
    }).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!locationId) return;
    api(`/pos/locations/${locationId}/registers`).then((d) => {
      setRegisters(d.registers);
      if (d.registers.length) setRegisterId(d.registers[0].register_id);
    }).catch((e) => setError(e.message));
  }, [locationId]);

  const refreshSession = useCallback(() => {
    if (!registerId) return;
    api(`/pos/register/current?register_id=${registerId}`)
      .then((d) => setSession(d.session))
      .catch((e) => setError(e.message))
      .finally(() => setSessionChecked(true));
  }, [registerId]);

  useEffect(() => { refreshSession(); }, [refreshSession]);

  // No open session for this register? Open one automatically with a $0
  // float rather than making the cashier fill out a form on every visit —
  // there's no register-close/cash-reconciliation screen in this app yet to
  // make tracking a real opening float meaningful, so the manual step was
  // pure friction. The session row itself still gets created normally
  // (register_session_id still ties every sale to a shift), so reconciliation
  // tooling can be added later without a schema change.
  useEffect(() => {
    if (!sessionChecked || session || !registerId || busy) return;
    openRegister(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionChecked, session, registerId]);

  useEffect(() => { searchRef.current?.focus(); }, [session]);

  useEffect(() => {
    api("/pos/catalog/categories").then((d) => setCategories(d.categories || [])).catch(() => {});
    api("/pos/catalog/products?active_only=true&page_size=500").then((d) => setCatalog(d.products || [])).catch(() => {});
    api("/pos/discounts/?active_only=true").then((d) => setDiscounts(d.discounts || [])).catch(() => {});
  }, []);

  const runSearch = useCallback((q) => {
    if (!q) { setProducts([]); return; }
    const params = q.length >= 8 && /^\d+$/.test(q) ? `barcode=${encodeURIComponent(q)}` : `q=${encodeURIComponent(q)}`;
    api(`/pos/catalog/products/search?${params}`)
      .then((d) => setProducts(d.products.filter(Boolean)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 200);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  useEffect(() => {
    if (!customerQuery) { setCustomerResults([]); return; }
    const t = setTimeout(() => {
      api(`/pos/customers/search?q=${encodeURIComponent(customerQuery)}`)
        .then((d) => setCustomerResults(d.results))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [customerQuery]);

  function addToCart(product, variantId = null) {
    const variant = product.variants?.find((v) => v.variant_id === variantId);
    const key = `${product.product_id}-${variantId || 0}-${Date.now()}`;
    setCart((c) => [...c, {
      key, item_type: "product", product_id: product.product_id, variant_id: variantId,
      description: variant ? `${product.name} — ${variant.variant_name}` : product.name,
      quantity: 1, unit_price: Number(variant?.price ?? product.selling_price),
    }]);
    setQuery(""); setProducts([]);
    searchRef.current?.focus();
  }

  function addCustomItem() {
    const description = window.prompt("Custom item description:");
    if (!description) return;
    const price = parseFloat(window.prompt("Price:", "0") || "0");
    setCart((c) => [...c, {
      key: `custom-${Date.now()}`, item_type: "custom", description, quantity: 1, unit_price: price,
    }]);
  }

  function updateQty(key, delta) {
    setCart((c) => c.map((it) => it.key === key ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it));
  }
  function removeItem(key) {
    setCart((c) => c.filter((it) => it.key !== key));
  }

  async function selectCustomer(r) {
    try {
      const d = await api(`/pos/customers/resolve?customer_type=${r.customer_type}&ref_id=${r.ref_id}`, { method: "POST" });
      setCustomer({ mapping_id: d.mapping_id, display_name: d.display_name });
      setCustomerQuery(""); setCustomerResults([]);
    } catch (e) { setError(e.message); }
  }

  const subtotal = cart.reduce((s, it) => s + it.unit_price * it.quantity, 0);
  const selectedDiscount = discounts.find((d) => String(d.discount_id) === String(selectedDiscountId));
  const estDiscount = (() => {
    let amt = 0;
    if (selectedDiscount && selectedDiscount.scope === "order") {
      amt = selectedDiscount.type === "percentage" ? subtotal * (Number(selectedDiscount.value) / 100) : Number(selectedDiscount.value);
      if (selectedDiscount.max_discount) amt = Math.min(amt, Number(selectedDiscount.max_discount));
    }
    if (appliedCustomDiscount) {
      const base = subtotal - amt;
      amt += appliedCustomDiscount.type === "percentage" ? base * (Number(appliedCustomDiscount.value) / 100) : Number(appliedCustomDiscount.value);
    }
    return Math.max(0, Math.min(amt, subtotal));
  })();

  async function openRegister(overrideCash) {
    const cash = overrideCash !== undefined ? overrideCash : parseFloat(openingCash || "0");
    setBusy(true); setError("");
    try {
      const d = await api("/pos/register/open", {
        method: "POST",
        body: JSON.stringify({ register_id: registerId, opening_cash: cash }),
      });
      setSession({ session_id: d.session_id, opening_cash: cash });
      refreshSession();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  function applyCustomDiscount() {
    if (!customDiscountValue || Number(customDiscountValue) <= 0) return;
    setAppliedCustomDiscount({ type: customDiscountType, value: Number(customDiscountValue), reason: customDiscountReason });
    setCustomDiscountOpen(false);
  }

  async function payNow() {
    if (!cart.length) return;
    setBusy(true); setError("");
    try {
      const created = await api("/pos/orders/", {
        method: "POST",
        headers: { "Idempotency-Key": `${session.session_id}-${Date.now()}` },
        body: JSON.stringify({
          location_id: locationId,
          register_session_id: session.session_id,
          customer_mapping_id: customer?.mapping_id || null,
          order_discount_id: selectedDiscountId ? Number(selectedDiscountId) : null,
          custom_discount_type: appliedCustomDiscount?.type || null,
          custom_discount_value: appliedCustomDiscount?.value || null,
          custom_discount_reason: appliedCustomDiscount?.reason || null,
          notes: notes || null,
          items: cart.map((it) => ({
            item_type: it.item_type, product_id: it.product_id, variant_id: it.variant_id,
            description: it.item_type === "custom" ? it.description : undefined,
            custom_price: it.item_type === "custom" ? it.unit_price : undefined,
            quantity: it.quantity,
          })),
        }),
      });
      const full = await api(`/pos/orders/${created.order_id}`);
      setOrder(full.order);
      setPayMethod("cash");
      setCheckoutOpen(true);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function payCash() {
    setBusy(true); setError("");
    try {
      await api(`/pos/orders/${order.order_id}/payments`, {
        method: "POST",
        body: JSON.stringify({ method: "cash", amount: Number(order.grand_total) }),
      });
      finishSale();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  // Mount the Stripe card element once the checkout modal switches to "card".
  useEffect(() => {
    if (!checkoutOpen || payMethod !== "card" || !STRIPE_PK || cardMountedRef.current) return;
    let cancelled = false;
    import("@stripe/stripe-js").then(({ loadStripe }) => loadStripe(STRIPE_PK)).then((stripe) => {
      if (cancelled || !stripe) return;
      stripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create("card", {
        style: { base: { fontSize: "15px", color: isDark ? "#e2e8f0" : "#1a2840",
          "::placeholder": { color: isDark ? "rgba(255,255,255,0.35)" : "rgba(3,78,123,0.40)" } } },
      });
      const el = document.getElementById("pos-card-element");
      if (el) {
        card.mount("#pos-card-element");
        cardElRef.current = card;
        cardMountedRef.current = true;
        setCardReady(true);
      }
    });
    return () => { cancelled = true; };
  }, [checkoutOpen, payMethod, isDark]);

  async function payCard() {
    if (!stripeRef.current || !cardElRef.current) {
      setError("Card terminal not ready — please wait a moment."); return;
    }
    setBusy(true); setError("");
    try {
      const intent = await api(`/pos/orders/${order.order_id}/payments/create-intent`, { method: "POST" });
      const { paymentIntent, error: stripeError } = await stripeRef.current.confirmCardPayment(
        intent.client_secret,
        { payment_method: { card: cardElRef.current } },
      );
      if (stripeError) throw new Error(stripeError.message);
      if (paymentIntent.status !== "succeeded") throw new Error(`Payment ${paymentIntent.status} — please try again.`);
      await api(`/pos/orders/${order.order_id}/payments`, {
        method: "POST",
        body: JSON.stringify({
          method: "stripe", amount: Number(order.grand_total),
          stripe_payment_intent_id: paymentIntent.id,
        }),
      });
      finishSale();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  function finishSale() {
    setCart([]); setCustomer(null); setNotes(""); setOrder(null); setCheckoutOpen(false);
    setSelectedDiscountId(""); setAppliedCustomDiscount(null);
    setCustomDiscountType("percentage"); setCustomDiscountValue(""); setCustomDiscountReason("");
    setPayMethod("cash");
    if (cardElRef.current) { try { cardElRef.current.unmount(); } catch { /* already unmounted */ } }
    cardElRef.current = null; cardMountedRef.current = false; setCardReady(false);
  }

  const card = { background: T.glass, border: `1px solid ${T.glassBdr}`, borderRadius: 14, padding: 16 };

  // No manual "Open Register" gate — the effect above opens a $0-float
  // session automatically. This only renders for the brief moment before
  // that finishes (or if it ever fails, so the cashier isn't stuck staring
  // at nothing).
  if (!session) {
    return (
      <div style={{ maxWidth: 420, margin: "60px auto", ...card, textAlign: "center" }}>
        {error ? (
          <>
            <h2 style={{ color: T.textPrimary, marginTop: 0 }}>Couldn't open the register</h2>
            <div style={{ color: T.red, marginBottom: 14 }}>{error}</div>
            <PrimaryButton style={{ width: "100%" }} disabled={busy || !registerId} onClick={() => openRegister(0)}>
              {busy ? "Opening…" : "Retry"}
            </PrimaryButton>
          </>
        ) : (
          <div style={{ color: T.textSub }}>Opening register…</div>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: 16, height: "calc(100dvh - 140px)", color: T.textPrimary }}>
      {/* LEFT — catalog */}
      <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            ref={searchRef} autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products or scan barcode…"
            style={{ flex: 1, padding: "12px 14px", borderRadius: 10, fontSize: 15, border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText }}
          />
          <GhostButton size="sm" onClick={addCustomItem}>+ Custom Item</GhostButton>
        </div>
        {error && <div style={{ color: T.red }}>{error}</div>}

        {query ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, overflowY: "auto" }}>
            {products.map((p) => (
              <div key={p.product_id} style={{ ...card, cursor: "pointer", padding: 12 }}
                   onClick={() => (p.variants?.length ? addToCart(p, p.variants[0].variant_id) : addToCart(p))}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name}</div>
                <div style={{ color: T.textSub, fontSize: 12, marginTop: 4 }}>{p.sku}</div>
                <div style={{ color: T.green || "#059669", fontWeight: 800, marginTop: 8 }}>{money(p.selling_price, currency)}</div>
              </div>
            ))}
            {!products.length && <div style={{ color: T.textMuted }}>No matches.</div>}
          </div>
        ) : selectedCategoryId !== null ? (
          <>
            <GhostButton size="sm" style={{ alignSelf: "flex-start" }} onClick={() => setSelectedCategoryId(null)}>
              ← Categories
            </GhostButton>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, overflowY: "auto" }}>
              {catalog
                .filter((p) => (selectedCategoryId === "uncategorized" ? !p.category_id : p.category_id === selectedCategoryId))
                .map((p) => (
                  <div key={p.product_id} style={{ ...card, cursor: "pointer", padding: 12 }}
                       onClick={() => (p.variants?.length ? addToCart(p, p.variants[0].variant_id) : addToCart(p))}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name}</div>
                    <div style={{ color: T.textSub, fontSize: 12, marginTop: 4 }}>{p.sku}</div>
                    <div style={{ color: T.green || "#059669", fontWeight: 800, marginTop: 8 }}>{money(p.selling_price, currency)}</div>
                  </div>
                ))}
            </div>
          </>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, overflowY: "auto" }}>
            {categories.map((cat) => {
              const count = catalog.filter((p) => p.category_id === cat.category_id).length;
              return (
                <div key={cat.category_id} style={{ ...card, cursor: "pointer", padding: 16, textAlign: "center" }}
                     onClick={() => setSelectedCategoryId(cat.category_id)}>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>{cat.name}</div>
                  <div style={{ color: T.textSub, fontSize: 12, marginTop: 6 }}>{count} item{count === 1 ? "" : "s"}</div>
                </div>
              );
            })}
            {catalog.some((p) => !p.category_id) && (
              <div style={{ ...card, cursor: "pointer", padding: 16, textAlign: "center" }}
                   onClick={() => setSelectedCategoryId("uncategorized")}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>Uncategorized</div>
                <div style={{ color: T.textSub, fontSize: 12, marginTop: 6 }}>
                  {catalog.filter((p) => !p.category_id).length} items
                </div>
              </div>
            )}
            {!categories.length && !catalog.length && <div style={{ color: T.textMuted }}>No products yet — add some in POS Catalog.</div>}
          </div>
        )}
      </div>

      {/* RIGHT — cart */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", ...card, minWidth: 320 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <strong>Cart</strong>
          {/* Customer lookup — turned off for now (customer_mapping_id stays null),
              pending redesign alongside the customer loyalty program. */}
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {cart.length === 0 && <div style={{ color: T.textMuted, fontSize: 13, marginTop: 20 }}>Cart is empty — search or scan to add items.</div>}
          {cart.map((it) => (
            <div key={it.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.divider}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{it.description}</div>
                <div style={{ fontSize: 12, color: T.textSub }}>{money(it.unit_price, currency)} × {it.quantity}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <GhostButton size="sm" onClick={() => updateQty(it.key, -1)}>−</GhostButton>
                <span>{it.quantity}</span>
                <GhostButton size="sm" onClick={() => updateQty(it.key, 1)}>+</GhostButton>
                <GhostButton size="sm" style={{ color: T.red }} onClick={() => removeItem(it.key)}>✕</GhostButton>
              </div>
            </div>
          ))}
        </div>

        <div style={{ borderTop: `1px solid ${T.divider}`, marginTop: 10, paddingTop: 10 }}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, color: T.textSub }}>Discount</label>
            <select
              value={selectedDiscountId}
              onChange={(e) => setSelectedDiscountId(e.target.value)}
              style={{ width: "100%", padding: "7px 8px", borderRadius: 8, fontSize: 13, border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, marginTop: 4 }}
            >
              <option value="">No discount</option>
              {discounts.map((d) => (
                <option key={d.discount_id} value={d.discount_id}>
                  {d.name} ({d.type === "percentage" ? `${d.value}%` : money(d.value, currency)}){d.code ? ` — ${d.code}` : ""}
                </option>
              ))}
            </select>
            {isAdminRole && (
              appliedCustomDiscount ? (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, fontSize: 12 }}>
                  <span style={{ color: T.textSub }}>
                    Custom: {appliedCustomDiscount.type === "percentage" ? `${appliedCustomDiscount.value}%` : money(appliedCustomDiscount.value, currency)}
                  </span>
                  <GhostButton size="sm" style={{ color: T.red, padding: "2px 8px" }} onClick={() => setAppliedCustomDiscount(null)}>Remove</GhostButton>
                </div>
              ) : (
                <GhostButton size="sm" style={{ marginTop: 6, width: "100%" }} onClick={() => setCustomDiscountOpen(true)}>
                  + Custom Discount (Admin)
                </GhostButton>
              )
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.textSub }}>
            <span>Subtotal</span><span>{money(subtotal, currency)}</span>
          </div>
          {estDiscount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.red }}>
              <span>Discount</span><span>-{money(estDiscount, currency)}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: 20, marginTop: 8 }}>
            <span>Total (est.)</span><span>{money(subtotal - estDiscount, currency)}</span>
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Tax computed at checkout.</div>
          <PrimaryButton style={{ width: "100%", marginTop: 12 }} disabled={busy || !cart.length} onClick={payNow}>
            {busy ? "Processing…" : "PAY NOW"}
          </PrimaryButton>
        </div>
      </div>

      {customDiscountOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
          <div style={{ ...card, width: 360, background: isDark ? "#0f2436" : "#fff" }}>
            <h3 style={{ marginTop: 0 }}>Custom Discount</h3>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>Admin / Super Admin override — logged to the audit trail.</div>
            <label style={{ fontSize: 12, color: T.textSub }}>Type</label>
            <select value={customDiscountType} onChange={(e) => setCustomDiscountType(e.target.value)}
                    style={{ width: "100%", padding: 8, marginTop: 4, marginBottom: 10, borderRadius: 8, boxSizing: "border-box" }}>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed amount</option>
            </select>
            <label style={{ fontSize: 12, color: T.textSub }}>Value</label>
            <input type="number" value={customDiscountValue} onChange={(e) => setCustomDiscountValue(e.target.value)}
                   placeholder={customDiscountType === "percentage" ? "e.g. 10" : "e.g. 5.00"}
                   style={{ width: "100%", padding: 8, marginTop: 4, marginBottom: 10, borderRadius: 8, boxSizing: "border-box" }} />
            <label style={{ fontSize: 12, color: T.textSub }}>Reason</label>
            <input value={customDiscountReason} onChange={(e) => setCustomDiscountReason(e.target.value)}
                   placeholder="e.g. Manager goodwill discount"
                   style={{ width: "100%", padding: 8, marginTop: 4, marginBottom: 14, borderRadius: 8, boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <GhostButton style={{ flex: 1 }} onClick={() => setCustomDiscountOpen(false)}>Cancel</GhostButton>
              <PrimaryButton style={{ flex: 1 }} disabled={!customDiscountValue} onClick={applyCustomDiscount}>Apply</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {checkoutOpen && order && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div style={{ ...card, width: 420, background: isDark ? "#0f2436" : "#fff" }}>
            <h3 style={{ marginTop: 0 }}>Order {order.order_number}</h3>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span>Subtotal</span><span>{money(order.subtotal, currency)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span>Discount</span><span>-{money(order.discount_total, currency)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span>Tax</span><span>{money(order.tax_total, currency)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: 18, marginBottom: 14 }}><span>Total</span><span>{money(order.grand_total, currency)}</span></div>

            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {payMethod === "cash" ? (
                <PrimaryButton style={{ flex: 1, padding: "8px 12px", fontSize: 13 }} onClick={() => setPayMethod("cash")}>Cash</PrimaryButton>
              ) : (
                <GhostButton style={{ flex: 1, padding: "8px 12px", fontSize: 13 }} onClick={() => setPayMethod("cash")}>Cash</GhostButton>
              )}
              {payMethod === "card" ? (
                <PrimaryButton style={{ flex: 1, padding: "8px 12px", fontSize: 13 }}
                        disabled={!STRIPE_PK} title={STRIPE_PK ? undefined : "Stripe publishable key not configured"}
                        onClick={() => setPayMethod("card")}>Card</PrimaryButton>
              ) : (
                <GhostButton style={{ flex: 1, padding: "8px 12px", fontSize: 13 }}
                        disabled={!STRIPE_PK} title={STRIPE_PK ? undefined : "Stripe publishable key not configured"}
                        onClick={() => setPayMethod("card")}>Card</GhostButton>
              )}
            </div>

            {payMethod === "card" && (
              <div style={{ marginBottom: 14 }}>
                <div id="pos-card-element" style={{ padding: 12, borderRadius: 8, border: `1px solid ${T.inputBdr}`, background: T.inputBg }} />
                {!cardReady && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>Loading card terminal…</div>}
              </div>
            )}

            {error && <div style={{ color: T.red, marginBottom: 10 }}>{error}</div>}
            {payMethod === "cash" ? (
              <PrimaryButton style={{ width: "100%", marginBottom: 8 }} disabled={busy} onClick={payCash}>
                {busy ? "Processing…" : `Pay Cash — ${money(order.grand_total, currency)}`}
              </PrimaryButton>
            ) : (
              <PrimaryButton style={{ width: "100%", marginBottom: 8 }} disabled={busy || !cardReady} onClick={payCard}>
                {busy ? "Processing…" : `Charge Card — ${money(order.grand_total, currency)}`}
              </PrimaryButton>
            )}
            <GhostButton style={{ width: "100%" }} onClick={() => setCheckoutOpen(false)}>Close</GhostButton>
          </div>
        </div>
      )}
    </div>
  );
}
